import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { 
  Upload, 
  FileText, 
  Download, 
  Trash2, 
  Edit, 
  Plus, 
  Search, 
  Filter,
  Calendar,
  AlertCircle,
  CheckCircle,
  Clock,
  X
} from 'lucide-react';
import { circularService, Circular } from '../services/circularService';
import { apiService } from '../services/api';

const CircularManagement: React.FC = () => {
  const [circulars, setCirculars] = useState<Circular[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadData, setUploadData] = useState({
    circularDate: new Date().toISOString().split('T')[0],
    issuedBy: '',
    refNo: '',
    title: '',
    bolisActive: true
  });

  // Load circulars on component mount and subscribe to changes
  useEffect(() => {
    loadCirculars();
    
    // Subscribe to changes in the shared service
    const unsubscribe = circularService.subscribe(() => {
      loadCirculars();
    });
    
    // Cleanup subscription on unmount
    return unsubscribe;
  }, []);

  const loadCirculars = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load circulars from the shared service
      const circularsData = await circularService.getCirculars();
      setCirculars(circularsData);
    } catch (error) {
      // Error loading circulars
      setError('Failed to load circulars');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setError('Please select a PDF file only.');
        return;
      }
      if (file.size > 100 * 1024 * 1024) { // 100MB limit
        setError('File size must be less than 100MB.');
        return;
      }
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !uploadData.title.trim()) {
      setError('Please select a file and enter a title.');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      
      // Simulate upload process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Add circular to the shared service
      const newCircular = await circularService.addCircular(
        uploadData.circularDate, uploadData.issuedBy, uploadData.refNo,
        uploadData.title, selectedFile, uploadData.bolisActive
      );

      setSuccess('Circular uploaded successfully!');

      // Reset form
      setSelectedFile(null);
      setUploadData({ circularDate: new Date().toISOString().split('T')[0], issuedBy: '', refNo: '', title: '', bolisActive: true });
      setShowUploadModal(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      // Error uploading circular
      setError('Failed to upload circular. Please try again.');
    } finally {
      setUploading(false);
    }
  };



  const handleDownload = async (circular: Circular) => {
    try {
      await circularService.downloadFile(circular.strGUID);
      setSuccess(`Downloading ${circular.strSubject}...`);
      setTimeout(() => setSuccess(null), 2000);
    } catch (error) {
      // Error downloading circular
      setError('Failed to download circular. Please try again.');
    }
  };

  const handleDelete = async (circularId: string) => {
    if (!window.confirm('Are you sure you want to delete this circular? This action cannot be undone.')) {
      return;
    }

    try {
      // Simulate delete process
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Delete from shared service
      const success = await circularService.deleteCircular(circularId);
      if (success) {
        setSuccess('Circular deleted successfully!');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to delete circular.');
      }
    } catch (error) {
      // Error deleting circular
      setError('Failed to delete circular. Please try again.');
    }
  };

  const handleToggleStatus = async (circular: Circular) => {
    try {
      const newStatus = !circular.bolisActive;
      await apiService.updateCircularStatus(circular.strGUID, newStatus);
      
      // Update the circular in the local state
      setCirculars(prevCirculars => 
        prevCirculars.map(c => 
          c.strGUID === circular.strGUID 
            ? { ...c, bolisActive: newStatus }
            : c
        )
      );
      
      setSuccess(`Circular ${newStatus ? 'activated' : 'deactivated'} successfully!`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      // Error toggling circular status
      setError('Failed to update circular status. Please try again.');
    }
  };

  const formatFileSize = (bytes: number): string => {
    return circularService.formatFileSize(bytes);
  };

  const formatDate = (dateString: string): string => {
    return circularService.formatDate(dateString);
  };

  const filteredCirculars = circulars.filter(circular => {
    const term = searchTerm.toLowerCase();
    return circular.strSubject.toLowerCase().includes(term) ||
      circular.strIssuedBy.toLowerCase().includes(term) ||
      circular.strRefNo.toLowerCase().includes(term);
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
            <FileText className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Important Circulars</h1>
            <p className="text-gray-600">Manage and upload important regulatory circulars and updates</p>
          </div>
        </div>
        <Button 
          onClick={() => setShowUploadModal(true)}
          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white border-0 shadow-lg transition-all duration-200"
        >
          <Plus className="h-4 w-4 mr-2" />
          Upload Circular
        </Button>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="p-4 bg-gradient-to-r from-green-50 to-green-100 border-2 border-green-200 rounded-xl shadow-lg">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3">
              <CheckCircle className="w-5 h-5 text-white" />
            </div>
            <span className="text-green-800 font-medium">{success}</span>
          </div>
        </div>
      )}

      {error && (
        <div className="p-4 bg-gradient-to-r from-red-50 to-red-100 border-2 border-red-200 rounded-xl shadow-lg">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center mr-3">
              <AlertCircle className="w-5 h-5 text-white" />
            </div>
            <span className="text-red-800 font-medium">{error}</span>
          </div>
        </div>
      )}

      {/* Search and Filter */}
      <Card className="border-2 border-blue-100 shadow-lg">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  placeholder="Search circulars..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 h-12 border-2 border-gray-200 focus:border-blue-400 rounded-xl transition-all duration-200"
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button 
                variant="outline" 
                size="sm"
                className="border-2 border-blue-200 hover:border-blue-400 transition-all duration-200 h-12 px-6"
              >
                <Filter className="h-4 w-4 mr-2" />
                Filter
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Loading State */}
      {loading && (
        <Card className="border-2 border-blue-100 shadow-lg">
          <CardContent className="p-12 text-center">
            <div className="flex flex-col items-center space-y-4">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-white border-t-transparent"></div>
              </div>
              <p className="text-lg font-medium text-gray-700">Loading circulars...</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Circulars List */}
      {!loading && (
        <Card className="border-2 border-blue-100 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-blue-200 p-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-xl font-bold text-gray-900">Circulars ({filteredCirculars.length})</CardTitle>
                <CardDescription className="text-gray-600">
                  Important regulatory circulars and updates
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {filteredCirculars.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FileText className="h-10 w-10 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Circulars Found</h3>
                <p className="text-gray-600">
                  {searchTerm ? 'Try adjusting your search terms' : 'Upload your first circular to get started'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredCirculars.map((circular) => (
                  <div key={circular.strGUID} className="group hover:shadow-lg transition-all duration-300 border-2 border-gray-100 hover:border-blue-200 rounded-xl p-6 bg-white">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300">
                          <FileText className="h-6 w-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg text-gray-900 group-hover:text-blue-600 transition-colors">
                            {circular.strSubject}
                          </h3>
                          {circular.strRefNo && (
                            <p className="text-sm text-gray-600 mt-1">Ref: {circular.strRefNo}</p>
                          )}

                          <div className="flex flex-wrap items-center gap-2 mt-3">
                            <span className="flex items-center text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                              <Calendar className="h-4 w-4 mr-1" />
                              {formatDate(circular.dtCircularDate)}
                            </span>
                            {circular.strIssuedBy && (
                              <span className="text-sm bg-purple-100 text-purple-700 px-3 py-1 rounded-full">
                                {circular.strIssuedBy}
                              </span>
                            )}
                            <span className="text-sm text-gray-500 bg-blue-100 text-blue-700 px-3 py-1 rounded-full">
                              {circular.strFileType.toUpperCase()}
                            </span>
                            <span className="text-sm text-gray-500">
                              {formatFileSize(circular.fileSize)}
                            </span>
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                              circular.bolisActive
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {circular.bolisActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownload(circular)}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white border-0 shadow-lg transition-all duration-200"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleStatus(circular)}
                          className={`border-2 transition-all duration-200 ${
                            circular.bolisActive 
                              ? 'border-green-300 text-green-600 hover:bg-green-50' 
                              : 'border-red-300 text-red-600 hover:bg-red-50'
                          }`}
                        >
                          {circular.bolisActive ? 'Active' : 'Inactive'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(circular.strGUID)}
                          className="border-2 border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400 transition-all duration-200"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-5 w-full max-w-xl border-2 border-blue-100 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow">
                  <Upload className="h-4 w-4 text-white" />
                </div>
                <h2 className="text-lg font-bold text-gray-900">Upload Circular</h2>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowUploadModal(false)}
                className="hover:bg-gray-100 rounded-full p-1"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-3">
              {/* Row 1: Date + Issued By */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="circularDate" className="text-xs font-medium text-gray-700 mb-1 block">Circular Date *</Label>
                  <Input
                    id="circularDate"
                    type="date"
                    value={uploadData.circularDate}
                    onChange={(e) => setUploadData({...uploadData, circularDate: e.target.value})}
                    className="h-9 border border-gray-300 focus:border-blue-400 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="issuedBy" className="text-xs font-medium text-gray-700 mb-1 block">Issued By</Label>
                  <Input
                    id="issuedBy"
                    value={uploadData.issuedBy}
                    onChange={(e) => setUploadData({...uploadData, issuedBy: e.target.value})}
                    placeholder="e.g. SEBI, NSE, BSE"
                    className="h-9 border border-gray-300 focus:border-blue-400 rounded-lg text-sm"
                  />
                </div>
              </div>

              {/* Row 2: Ref No */}
              <div>
                <Label htmlFor="refNo" className="text-xs font-medium text-gray-700 mb-1 block">Reference No.</Label>
                <Input
                  id="refNo"
                  value={uploadData.refNo}
                  onChange={(e) => setUploadData({...uploadData, refNo: e.target.value})}
                  placeholder="e.g. SEBI/HO/MIRSD/..."
                  className="h-9 border border-gray-300 focus:border-blue-400 rounded-lg text-sm"
                />
              </div>

              {/* Row 3: Subject */}
              <div>
                <Label htmlFor="title" className="text-xs font-medium text-gray-700 mb-1 block">Subject *</Label>
                <Input
                  id="title"
                  value={uploadData.title}
                  onChange={(e) => setUploadData({...uploadData, title: e.target.value})}
                  placeholder="Enter circular subject"
                  className="h-9 border border-gray-300 focus:border-blue-400 rounded-lg text-sm"
                />
              </div>

              {/* Row 4: Status (inline) */}
              <div>
                <Label className="text-xs font-medium text-gray-700 mb-1 block">Status</Label>
                <div className="flex gap-3">
                  <label className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg hover:border-blue-300 cursor-pointer flex-1">
                    <input
                      type="radio"
                      name="isActive"
                      value="true"
                      checked={uploadData.bolisActive === true}
                      onChange={(e) => setUploadData({...uploadData, bolisActive: e.target.value === 'true'})}
                      className="text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                    />
                    <div className="w-2.5 h-2.5 bg-green-500 rounded-full"></div>
                    <span className="text-sm text-gray-700">Active</span>
                  </label>
                  <label className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg hover:border-blue-300 cursor-pointer flex-1">
                    <input
                      type="radio"
                      name="isActive"
                      value="false"
                      checked={uploadData.bolisActive === false}
                      onChange={(e) => setUploadData({...uploadData, bolisActive: e.target.value === 'true'})}
                      className="text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                    />
                    <div className="w-2.5 h-2.5 bg-red-500 rounded-full"></div>
                    <span className="text-sm text-gray-700">Inactive</span>
                  </label>
                </div>
              </div>

              {/* Row 5: File Upload */}
              <div>
                <Label htmlFor="file" className="text-xs font-medium text-gray-700 mb-1 block">PDF File *</Label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 text-center hover:border-blue-400 transition-colors">
                  <Input
                    id="file"
                    type="file"
                    accept=".pdf"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <label htmlFor="file" className="cursor-pointer flex flex-col items-center">
                    <Upload className="h-6 w-6 text-gray-400 mb-1" />
                    <span className="text-sm font-medium text-blue-600 hover:text-blue-700">Choose a PDF file</span>
                    <span className="text-xs text-gray-500">Max 100MB</span>
                  </label>
                </div>
              </div>

              {selectedFile && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center space-x-3">
                  <FileText className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-blue-900 truncate">{selectedFile.name}</p>
                    <p className="text-xs text-blue-600">{formatFileSize(selectedFile.size)}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-end space-x-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowUploadModal(false)}
                  disabled={uploading}
                  className="border-2 border-gray-300 hover:border-gray-400 transition-all duration-200 px-6"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpload}
                  disabled={uploading || !selectedFile || !uploadData.title.trim()}
                  className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white border-0 shadow-lg transition-all duration-200 px-6"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CircularManagement; 